<script setup>
/**
 * Test it — chat with the assistant before your customers do. THE PLAN, §4 P6.
 *
 * THE NAMED REGRESSION THIS SCREEN EXISTS TO KILL is one control: "Allow live tool calls"
 * was a raw `<input type="checkbox">` with no styling at all, so on the near-black page it
 * rendered as the platform's default — a BRIGHT WHITE FILLED SQUARE, the single lightest
 * object on the screen, in its UNCHECKED state (`shots/ours/playground-dark.png`). Two
 * defects in one: it was the loudest thing on a page it had no business dominating, and
 * because "off" looked filled it read as ON while it was OFF — on the one control that
 * decides whether the assistant really calls the owner's own systems. It is Chatwoot's
 * `components-next/switch/Switch.vue` now, whose track and knob are both `n-*` tokens, so
 * it follows the theme in both directions with no colour named anywhere in this tree.
 *
 * WHAT THE SCREEN IS, in the order it is read:
 *   1. one paragraph saying nothing here reaches a customer and that it costs credits;
 *   2. two collapsed setup panels — wording you have not saved, and who you are pretending
 *      to be — because most tests need neither and neither belongs above the chat;
 *   3. the thread, which opens with three messages drawn from THIS account's own content
 *      — what its customers asked and nobody answered, what it has already written an
 *      answer for — topped up with neutral ones when it has fewer than three, rather than
 *      with nine hundred pixels of nothing (the bar, `bar/captain-playground-dark.png`,
 *      is empty at that moment and it is the weakest part of it);
 *   4. the composer, DOCKED in the frame's `footer` slot, with the live-actions switch
 *      immediately above it.
 *
 * WHY THE SWITCH IS DOCKED AND NOT IN THE BODY. It is the only genuinely dangerous
 * control in the section, and its consequence is per message, not per session. A safety
 * toggle that has scrolled off the top of a long chat is how somebody places a real order
 * while believing they were rehearsing. It sits with the Send button, permanently, and the
 * sentence beside it changes with its state so that "on" and "off" can never read alike.
 *
 * B4 (P-2.10, P-2.11, P-2.12). The chat is `useTestSession`, the one send path this page shares
 * with the Check screen's "Try it before customers do" drawer. "Show it as" picks the channel the
 * reply is shaped for, and the options under a reply look and tap the way that channel handles
 * them. The collapsed customer form is now the live customer card: a right-hand column from
 * 1024px, a panel above the composer below that. It starts the way the chosen channel starts, and
 * after each reply it shows what the assistant saved about the customer.
 *
 * WHAT THIS PAGE DELIBERATELY DOES NOT DO: gate itself on a read. `is-fetching` stays
 * false and there is no error branch. All three things it fetches are SUPPORTING — the
 * saved wording for the optional override box, how much of today's testing budget is gone,
 * and the account's own opening messages —
 * and a chat you cannot start because a panel you did not open failed to load is a worse
 * screen than one whose panel says so. That is the inverse of Overview's rule, for the
 * inverse reason: there, every region was a fact about the account and an absence was a
 * lie; here, the page's whole job works with nothing loaded at all. A failed read draws
 * nothing, never a reassuring line.
 */
import { computed, nextTick, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import TestCustomerCard from '../components/TestCustomerCard.vue';
import TestThread from '../components/TestThread.vue';
import TestWordingBox from '../components/TestWordingBox.vue';
import WasloExplainer from '../components/WasloExplainer.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import WasloSelect from '../components/WasloSelect.vue';
import WasloTextArea from '../components/WasloTextArea.vue';
import AiConfirmDialog from '../components/AiConfirmDialog.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { refusalKey, useTestSession } from '../composables/useTestSession.js';
import { useWasloStatus } from '../composables/useWasloStatus.js';
import { statusCopyKey } from '../helpers/status.js';
import { hasArabicLetter } from '../helpers/bidi.js';
import { toIntlLocale } from '../helpers/intl.js';
import {
  cardAfterReply,
  contactPayloadOf,
  startingCard,
} from '../helpers/customer-card.js';
import { CHANNEL_NAME_KEY } from '../helpers/task-channels.js';
import {
  DEFAULT_TEST_CHANNEL,
  TEST_CHANNELS,
} from '../helpers/test-channel.js';
import { wasloApi } from '../api/client.js';

/** Below halfway the number is noise; from halfway it is a warning worth reading. */
const BUDGET_VISIBLE_FROM = 50;

const { t, locale } = useWasloI18n();
const accountId = useWasloAccountId();

const draft = ref('');
const liveActions = ref(false);

// ── "Show it as" (P-2.10) ──────────────────────────────────────────────────────────
const channel = ref(DEFAULT_TEST_CHANNEL);
const channelOptions = computed(() =>
  TEST_CHANNELS.map(kind => ({
    value: kind,
    label: t(CHANNEL_NAME_KEY[kind]),
  }))
);

const composerMessage = ref('');
const wordingMessage = ref('');
const customerMessage = ref('');
// Raised, never lowered. A rejection that names a box inside a collapsed panel has to
// open that panel or the owner is left with a refused send and nothing on screen saying
// why; closing it again on the next attempt would then hide the correction as she makes
// it. See the `isOpen` prop on both panels.
const wordingPanelOpen = ref(false);
const customerPanelOpen = ref(false);

// ── the opening messages ───────────────────────────────────────────────────────────
/**
 * The three chips on the empty thread, and where they come from. W-13.
 *
 * They were three FIXED lines about a perfume shop, shown to every account — a 12ml Dehn
 * Al Oud gift box and a delivery to Jahra — and the comment above this screen claimed
 * they were "messages the owner's own customers actually send", which they were not for
 * anyone but that one shop. A partner testing a photo studio asked us to confirm we had
 * not mistaken her business for something else.
 *
 * So they come from the account now (`GET /test/starters`: questions its customers asked
 * and nobody answered, and answers it has already written), and the three neutral lines
 * below are only what is left when it has nothing to offer yet — a brand-new account has
 * neither, and the screen still has to open on something.
 */
const STARTER_COUNT = 3;

const NEUTRAL_STARTERS = [
  'WASLO.TEST.THREAD.STARTER_ONE',
  'WASLO.TEST.THREAD.STARTER_TWO',
  'WASLO.TEST.THREAD.STARTER_THREE',
];

/**
 * THE ARABIC GUARANTEE, and the rule is exactly this: if no Arabic LETTER appears in the
 * final three, the LAST slot is spent on the Arabic neutral line.
 *
 * Kuwait writes both scripts and this screen is where an owner first sees that her
 * assistant is expected to. An account whose three real starters all happen to be Latin
 * would otherwise open on an all-English board and teach her the opposite — so it gives
 * up its third real line rather than the guarantee, and keeps the two most useful.
 *
 * LETTER, not "anything in the Arabic block" — see `hasArabicLetter`. The block test this
 * started as accepted "Are you open today؟", an English sentence typed on an Arabic
 * keyboard, and these lines are real customer messages now, so that sentence really
 * arrives and really would have cost the guarantee.
 *
 * The neutral line it falls back on is written in Arabic in BOTH message tables (it is a
 * sample CUSTOMER message, not dashboard copy), so this holds whichever language the
 * dashboard is in — and that is the load-bearing half. `vocabulary.spec.js` asserts the
 * en/ar pair is still identical, in both directions, so Latinising either one fails the
 * build rather than quietly emptying the guarantee for half the accounts.
 */
const ARABIC_STARTER = 'WASLO.TEST.THREAD.STARTER_THREE';

/**
 * Degrades silently by construction: nothing reads `error`, so a failed or empty read is
 * the same screen as an account with nothing to offer — three neutral lines. It is a
 * SUPPORTING read like the other two (see the header), and this one is not even allowed
 * to say so: a sentence apologising for missing examples is noise on a page whose job is
 * a chat, and the examples are there either way.
 */
const accountStarters = useWasloApi(signal =>
  wasloApi.getTestStarters(accountId.value, signal)
);

const starters = computed(() => {
  const chosen = [];
  const seen = new Set();
  // Trimmed and de-duplicated in one place, so the account's own line and a neutral one
  // that happens to read the same can never both appear — two identical chips also share
  // a `v-for` key.
  const add = value => {
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text || seen.has(text) || chosen.length >= STARTER_COUNT) return;
    seen.add(text);
    chosen.push(text);
  };

  (accountStarters.data.value ?? []).forEach(starter => add(starter.text));
  NEUTRAL_STARTERS.forEach(key => add(t(key)));

  if (chosen.length && !chosen.some(hasArabicLetter)) {
    const arabic = t(ARABIC_STARTER).trim();
    // `seen` is corrected too, not just `chosen`. Nothing appends after this today, and
    // the moment something does, a stale `seen` is how the overwritten line comes back as
    // a duplicate — and duplicates here are `v-for` keys.
    seen.delete(chosen[chosen.length - 1]);
    seen.add(arabic);
    chosen[chosen.length - 1] = arabic;
  }
  return chosen;
});

// ── the wording under test ─────────────────────────────────────────────────────────
// It fills the override box with what is stored, so the owner edits her own wording
// rather than staring at an empty box she has to paste into.
const config = useWasloApi(signal =>
  wasloApi.getConfig(accountId.value, signal)
);

const savedWording = ref(null);
const wordingDraft = ref('');
const wordingIsDefault = ref(false);

watch(config.data, value => {
  if (!value) return;
  savedWording.value = value.systemPrompt;
  wordingDraft.value = value.systemPrompt;
  wordingIsDefault.value = value.personaIsDefault === true;
});

const wordingUnavailable = computed(() => Boolean(config.error.value));

// ── today's testing budget ─────────────────────────────────────────────────────────
/**
 * The LAST of the page's three reads, and it follows the same rule as the other two: a
 * chat you cannot start because a supporting read failed is a worse screen than one that
 * says a little less. A failure here draws no line at all — never a reassuring one.
 */
const limits = useWasloApi(signal =>
  wasloApi.getLimits(accountId.value, signal)
);

/**
 * P-5.1: what REAL customers are getting right now, in the same words as the Overview. A
 * rehearsal here is not what they get when the assistant is practising, stopped or off, and
 * an owner testing replies deserves to know that before she reads one. Silent when unread.
 */
const status = useWasloStatus(accountId);
const customersLine = computed(() => {
  const state = status.account.value?.state;
  return state
    ? t('WASLO.STATUS.CUSTOMERS', { state: t(`${statusCopyKey(state)}.CHIP`) })
    : '';
});

// vue-i18n hands us Chatwoot's directory name (`pt_BR`), which `Intl` rejects outright.
const intlLocale = computed(() => toIntlLocale(locale.value));

/**
 * `style: 'percent'` takes a FRACTION, and the contract sends a whole 0–100 — so the
 * division is the unit conversion, not a rounding step. `Intl` rather than a hardcoded
 * `%` because the locale owns the sign, its position and the digits: `ar` resolves to
 * Latin digits on current ICU, so an Arabic account reads "62%", but `ar-EG` and `ar-KW`
 * resolve to Arabic-Indic and read "٦٢٪". Neither is ours to choose.
 *
 * Clamped the same way `TestTrace.vue` clamps a match score — see `OverviewPage.vue` for
 * why the clamp lives here and not in the reader.
 */
const formatPercent = whole =>
  new Intl.NumberFormat(intlLocale.value, {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.min(100, whole)) / 100);

const testBudgetLine = computed(() => {
  // A read that failed says nothing, and that includes a REFRESH that failed: the number
  // on screen was true when the page opened, and after a refusal it is precisely the
  // thing we have just discovered we cannot vouch for. Stale beside a contradicting
  // refusal is worse than absent.
  if (limits.error.value) return '';
  const used = limits.data.value?.test?.usedPercent;
  if (typeof used !== 'number' || used < BUDGET_VISIBLE_FROM) return '';
  return t('WASLO.TEST.LIMITS.TEST_BUDGET', { percent: formatPercent(used) });
});

/**
 * Send the override only when it actually differs from what is stored.
 *
 * Sending it always would be simpler and wrong: an unchanged override is still an
 * override upstream, so a later change to the saved wording would stop reaching a screen
 * that had been open since before it — the assistant would answer with wording the owner
 * had already replaced, and nothing would say so.
 */
const wordingIsChanged = computed(
  () => savedWording.value !== null && wordingDraft.value !== savedWording.value
);

// ── the simulated customer (P-2.12) ─────────────────────────────────────────────────────────

const card = ref(startingCard(DEFAULT_TEST_CHANNEL));
const cardChanged = ref([]);
const cardRefused = ref([]);
const resetCard = () => {
  card.value = startingCard(channel.value);
  cardChanged.value = [];
  cardRefused.value = [];
};

// ── sending ────────────────────────────────────────────────────────────────────────

/**
 * After a reply: the card becomes the server's card (every simulated save applied by the real
 * writer's rule), with what just changed marked. An older BFF sends no card and nothing moves.
 */
const onReply = reply => {
  const next = cardAfterReply(card.value, reply?.trace?.customer ?? null);
  card.value = next.card;
  cardChanged.value = next.changed;
  cardRefused.value = next.refused;
};

/**
 * After a refusal. A spending refusal IS news about the budget, so the line above must not go on
 * saying "62% used" beneath "today's testing budget is used up" (fire and forget). `fields` is
 * non-null only on `validation_failed`, keyed by the request-body keys this page sent, so each
 * sentence lands under the control that caused it, opening a collapsed panel if it has to.
 */
const onError = error => {
  if (refusalKey(error)) limits.reload();
  const fields = error?.fields ?? null;
  if (!fields) return;
  if (fields.message) composerMessage.value = fields.message;
  if (fields['instructionsOverride.instructions']) {
    wordingMessage.value = fields['instructionsOverride.instructions'];
    wordingPanelOpen.value = true;
  }
  const contactField = Object.keys(fields).find(name =>
    name.startsWith('contact.')
  );
  if (contactField) {
    customerMessage.value = fields[contactField];
    customerPanelOpen.value = true;
  }
};

const session = useTestSession({
  accountId,
  extras: () => {
    const contact = contactPayloadOf(card.value);
    return {
      liveActions: liveActions.value,
      channel: channel.value,
      ...(contact ? { contact } : {}),
      ...(wordingIsChanged.value
        ? { instructionsOverride: { instructions: wordingDraft.value } }
        : {}),
    };
  },
  onReply,
  onError,
});
const {
  charge: testCharge,
  turns,
  isSending,
  sendLabel,
  receiptText: testReceipt,
  sampleNote,
} = session;

/** P-2.12: the task's answers so far, from the newest reply that carried any. */
const cardAnswers = computed(() => {
  const withAnswers = [...turns.value]
    .reverse()
    .find(turn => turn.trace?.stepTask?.answers?.length);
  return withAnswers ? withAnswers.trace.stepTask.answers : [];
});

const thread = ref(null);

/**
 * Keep the newest turn in view.
 *
 * The scrollport belongs to `WasloPageLayout`, not to this page, so it is found by
 * attribute rather than passed down — the alternative is a ref threaded through a frame
 * whose whole point is that pages do not think about it. Both guards are real: there is
 * no scrollport at all in a unit mount, and `scrollTo` is not implemented in jsdom.
 */
const scrollToLatest = () => {
  const port = thread.value?.$el?.closest?.('[data-waslo-page-main]');
  if (!port || typeof port.scrollTo !== 'function') return;
  port.scrollTo({ top: port.scrollHeight, behavior: 'smooth' });
};

const useStarter = text => {
  draft.value = text;
  composerMessage.value = '';
};

const restart = () => {
  session.restart();
  composerMessage.value = '';
  cardChanged.value = [];
  cardRefused.value = [];
};

/**
 * A chat cannot move between channels, so a new channel is a new chat, and the card starts the
 * way that channel's customers arrive (a WhatsApp customer has a name and a phone; a website
 * visitor has nothing).
 */
watch(channel, () => {
  restart();
  resetCard();
});

/** One turn, from the composer or a tapped option, kept in view as it lands. */
const sendTurn = async (message, choice) => {
  nextTick(scrollToLatest);
  const outcome = await session.sendTurn(message, choice);
  nextTick(scrollToLatest);
  return outcome;
};

/**
 * Checked before EITHER kind of send, and deliberately here as well as on the BFF: an emptied
 * override box must never fall back to the saved wording.
 */
const wordingRefused = () => {
  if (wordingIsChanged.value && wordingDraft.value.trim() === '') {
    wordingMessage.value = t('WASLO.TEST.WORDING.EMPTY');
    wordingPanelOpen.value = true;
    return true;
  }
  return false;
};

const clearMessages = () => {
  composerMessage.value = '';
  wordingMessage.value = '';
  customerMessage.value = '';
};

const send = async () => {
  if (isSending.value) return;
  const message = draft.value.trim();
  clearMessages();
  if (!message) {
    composerMessage.value = t('WASLO.TEST.COMPOSER.EMPTY');
    return;
  }
  if (wordingRefused()) return;
  draft.value = '';
  if ((await sendTurn(message, null)) === 'cancelled') draft.value = message;
};

/** A tapped menu option: its title is the message, its id rides alongside. */
const choose = async option => {
  if (isSending.value) return;
  clearMessages();
  if (wordingRefused()) return;
  await sendTurn(option.title, { id: option.choiceId, title: option.title });
};

/** A tap on a channel that sends back only the words (Messenger, Telegram): a typed message. */
const say = async text => {
  if (isSending.value) return;
  clearMessages();
  if (wordingRefused()) return;
  await sendTurn(text, null);
};

/** Enter sends; Shift and Enter start a new line. IME composition is left alone — an
 *  Arabic or Chinese keyboard commits a candidate with Enter and would otherwise send
 *  half a word. */
const onComposerKeydown = event => {
  if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
  event.preventDefault();
  send();
};

// The switch changes what the words beside it mean, so the card it sits in changes too:
// ruby when it is armed, ordinary when it is rehearsing. One computed rather than two
// conditional classes, because two `outline-*` utilities on one element are resolved by
// stylesheet order, not by the order they are written.
const liveCardClass = computed(() =>
  liveActions.value
    ? 'outline-n-ruby-8 bg-n-ruby-2'
    : 'outline-n-weak bg-n-solid-1'
);
</script>

<template>
  <WasloPageLayout :header-title="t('WASLO.TEST.TITLE')">
    <!-- Top-right, where Captain puts its own reset control. Only once there is
         something to start again from. -->
    <template #search>
      <Button
        v-if="turns.length"
        :label="t('WASLO.TEST.RESTART')"
        icon="i-lucide-rotate-ccw"
        variant="outline"
        color="slate"
        size="sm"
        data-waslo-test-restart
        @click="restart"
      />
    </template>

    <template #body>
      <!-- P-2.12: the customer card is a right-hand column from 1024px; below that it is a
           panel above the composer (the footer). -->
      <div
        class="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start"
      >
        <div class="flex flex-col min-w-0 gap-6">
          <WasloExplainer
            :note="t('WASLO.TEST.SPOTLIGHT')"
            icon="i-lucide-message-circle-more"
          />
          <!-- C3 P-6.10: "Only when you're closed" does not apply here. Said, not hidden. -->
          <p
            v-if="config.data.value?.answerWhen === 'closed_only'"
            class="mb-0 text-sm text-n-slate-11"
            data-waslo-test-answer-when
          >
            {{ t('WASLO.ANSWER_WHEN.TEST_NOTE') }}
          </p>
          <p
            v-if="customersLine"
            class="mb-0 text-sm font-medium text-n-slate-12"
            data-waslo-test-customers
          >
            {{ customersLine }}
          </p>

          <!-- Only from halfway, and only ever a percentage. Testing has a budget of its
             own and spending it affects no customer — so this sits with the paragraph
             that explains the page, not with the refusals. -->
          <p
            v-if="testBudgetLine"
            class="mb-0 text-sm text-n-slate-11"
            data-waslo-test-budget
          >
            {{ testBudgetLine }}
          </p>

          <div class="flex flex-col gap-3">
            <TestWordingBox
              v-model="wordingDraft"
              :saved="savedWording"
              :is-default="wordingIsDefault"
              :is-unavailable="wordingUnavailable"
              :message="wordingMessage"
              :is-open="wordingPanelOpen"
            />
            <!-- P-2.10: the channel the reply is shaped for. A new channel is a new chat. -->
            <WasloSelect
              v-model="channel"
              class="sm:w-64"
              :label="t('WASLO.TEST.CHANNEL.LABEL')"
              :options="channelOptions"
              data-waslo-test-channel
            />
            <p
              v-if="channel === 'website'"
              class="mb-0 text-sm text-n-slate-11"
              data-waslo-test-channel-note
            >
              {{ t('WASLO.TEST.CHANNEL.WEBSITE_NOTE') }}
            </p>
          </div>

          <TestThread
            ref="thread"
            :turns="turns"
            :starters="starters"
            :busy="isSending"
            @use-starter="useStarter"
            @choose="choose"
            @say="say"
            @fetch-sample="session.fetchSample"
          />
          <p
            v-if="sampleNote"
            class="mb-0 text-sm text-n-slate-11"
            role="status"
            data-waslo-test-sample-note
          >
            {{ sampleNote }}
          </p>

          <!-- Clearance above the docked composer. The frame reserves 64px of its own; a
             chat ends in the message you are reading and wants more air than a form. -->
          <div class="h-10 shrink-0" aria-hidden="true" />
        </div>
        <aside
          class="hidden lg:block lg:sticky lg:top-4"
          data-waslo-test-card-column
        >
          <TestCustomerCard
            :card="card"
            :changed="cardChanged"
            :refused="cardRefused"
            :answers="cardAnswers"
            :message="customerMessage"
            @update:card="value => (card = value)"
            @reset="resetCard"
          />
        </aside>
      </div>
    </template>

    <template #footer>
      <div class="flex flex-col gap-3 py-4">
        <!-- P-2.12 below 1024px: the same card, folded above the composer. -->
        <div class="flex flex-col gap-2 lg:hidden" data-waslo-test-card-panel>
          <button
            type="button"
            class="self-start text-sm underline text-n-blue-11"
            :aria-expanded="customerPanelOpen"
            data-waslo-test-card-toggle
            @click="customerPanelOpen = !customerPanelOpen"
          >
            {{
              customerPanelOpen
                ? t('WASLO.TEST.CARD.HIDE')
                : t('WASLO.TEST.CARD.SHOW')
            }}
          </button>
          <TestCustomerCard
            v-if="customerPanelOpen"
            class="max-h-[40vh] overflow-y-auto"
            :card="card"
            :changed="cardChanged"
            :refused="cardRefused"
            :answers="cardAnswers"
            :message="customerMessage"
            @update:card="value => (card = value)"
            @reset="resetCard"
          />
        </div>
        <div
          class="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 outline outline-1 rounded-xl"
          :class="liveCardClass"
          data-waslo-live-actions
        >
          <Switch
            v-model="liveActions"
            :aria-label="t('WASLO.TEST.LIVE.LABEL')"
            class="shrink-0"
            data-waslo-live-switch
          />
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('WASLO.TEST.LIVE.LABEL') }}
          </span>
          <span
            class="min-w-0 text-sm"
            :class="liveActions ? 'text-n-ruby-11' : 'text-n-slate-11'"
            data-waslo-live-consequence
          >
            {{
              liveActions ? t('WASLO.TEST.LIVE.ON') : t('WASLO.TEST.LIVE.OFF')
            }}
          </span>
        </div>

        <div class="flex items-end gap-3">
          <WasloTextArea
            v-model="draft"
            class="flex-1"
            :label="t('WASLO.TEST.COMPOSER.LABEL')"
            :placeholder="t('WASLO.TEST.COMPOSER.PLACEHOLDER')"
            :disabled="isSending"
            min-height="2.25rem"
            max-height="10rem"
            data-waslo-composer
            @keydown="onComposerKeydown"
          >
            <template #labelAction>
              <span class="text-xs shrink-0 text-n-slate-10">
                {{ t('WASLO.TEST.COMPOSER.HINT') }}
              </span>
            </template>
          </WasloTextArea>
          <Button
            :label="sendLabel"
            variant="solid"
            color="blue"
            :is-loading="isSending"
            :disabled="isSending"
            data-waslo-send
            @click="send"
          />
        </div>

        <!-- Rendered here rather than inside the field: a message under the box would
             move the Send button down a line every time it appeared. -->
        <p
          v-if="composerMessage"
          class="mb-0 text-sm text-n-ruby-9"
          role="alert"
          data-waslo-composer-message
        >
          {{ composerMessage }}
        </p>
        <p
          v-if="testReceipt"
          class="mb-0 text-xs text-n-slate-11"
          data-waslo-ai-receipt
        >
          {{ testReceipt }}
        </p>
        <AiConfirmDialog :controller="testCharge" />
      </div>
    </template>
  </WasloPageLayout>
</template>
